import { DEFAULT_STUN_SERVERS, type UserSettings } from '@flowshare/protocol';

function isStunUrl(url: string): boolean {
  return /^stuns?:/i.test(url);
}

function isTurnUrl(url: string): boolean {
  return /^turns?:/i.test(url);
}

/**
 * Build the RTCPeerConnection configuration from user settings.
 *
 * STUN is enough for most networks; TURN is required when both peers sit
 * behind symmetric NATs or restrictive corporate firewalls. We never claim
 * universal connectivity without it.
 */
export function buildRtcConfiguration(
  settings: Pick<UserSettings, 'stunServers' | 'turn'>,
): RTCConfiguration {
  const iceServers: RTCIceServer[] = [];

  const stunUrls = (settings.stunServers.length > 0 ? settings.stunServers : DEFAULT_STUN_SERVERS)
    .filter(isStunUrl)
    .slice(0, 8);

  for (const urls of stunUrls) {
    iceServers.push({ urls });
  }

  const turn = settings.turn;
  if (turn) {
    const urls = turn.urls.filter(isTurnUrl).slice(0, 8);
    if (urls.length > 0) {
      iceServers.push({
        urls,
        ...(turn.username ? { username: turn.username } : {}),
        ...(turn.credential ? { credential: turn.credential } : {}),
      });
    }
  }

  return {
    iceServers,
    iceCandidatePoolSize: 4,
    bundlePolicy: 'max-bundle',
    rtcpMuxPolicy: 'require',
  };
}

export function describeIceSupport(settings: UserSettings): string {
  const turnEnabled = Boolean(settings.turn && settings.turn.urls.some(isTurnUrl));
  return turnEnabled ? 'STUN + TURN relay fallback' : 'STUN only (direct connections)';
}
